import React, { useCallback, useEffect, useRef, useState } from "react";

import InspectorInfoField from "@Components/groups/InspectorInfoField";
import ModuleAudienceRow from "@Components/groups/ModuleAudienceRow";
import { QueryKeys } from "@Constants";
import { IActivityDefinitions } from "@Models/IActivityDetails";
import { IAssetDetails, IAudienceDetails, IAudienceTagInstanceDetails, ILicenseDetails } from "@Models/ILicenseDetails";
import LicenseService from "@Services/LicenseService";
import { isNil, toPascalCase } from "@Utils";
import { CloseCircleFilled, ExportOutlined, LoadingOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import {
    Badge,
    Button,
    Cascader,
    Collapse,
    DatePicker,
    Divider,
    Empty,
    Form,
    Input,
    InputNumber,
    Popconfirm,
    Select,
    Switch,
    Tag,
    notification,
} from "antd";
import AwesomeDebouncePromise from "awesome-debounce-promise";
import moment from "moment/moment";

const { Panel } = Collapse;
const { Option } = Select;

interface ICascaderOption {
    label: React.ReactNode;
    value: string;
    children?: ICascaderOption[];
}

interface IAssetOption {
    value?: string | null;
    label: React.ReactNode;
    children?: IAssetOption[];
    isLeaf?: boolean;
    loading?: boolean;
}

interface IProps {
    fetching: boolean;
    licenses: ILicenseDetails[];
    current: ILicenseDetails;
    updateCurrent: (license: ILicenseDetails) => void;
    deleteCurrent: (license: ILicenseDetails) => void;
}

const LicenseEditor: React.FC<IProps> = ({ fetching, licenses, current, updateCurrent, deleteCurrent }: IProps) => {
    const [audiences, setAudiences] = useState<IAudienceDetails[]>([]);
    const [updating, setUpdating] = useState<boolean>(false);

    const assetQuantityOptions: IAssetOption[] = [];
    for (let i = 1; i <= 10; i++)
        assetQuantityOptions.push({ value: "new-" + i.toString(), label: "x" + i.toString(), isLeaf: true });
    const assetDefaultOptions: IAssetOption[] = [
        { value: "accepted", label: "Create", isLeaf: false, children: assetQuantityOptions },
        { value: "unassigned", label: "Unassigned", isLeaf: false },
    ];
    const [assetOptions, setAssetOptions] = useState<IAssetOption[]>(assetDefaultOptions);

    const stateRef = useRef<ILicenseDetails>();
    const queryClient = useQueryClient();
    const definitions = queryClient.getQueryData<IActivityDefinitions>([QueryKeys.ActivityDefinitions]);

    /**
     * Update the model with edited data.
     */
    const update = async () => {
        setUpdating(true);
        const service = new LicenseService();
        const res = await service.update(stateRef.current);
        setUpdating(false);
        if (!res.hasErrors) {
            notification.success({ message: "License updated." });
        } else {
            notification.error({ message: "Error updating license." });
        }
        await queryClient.invalidateQueries([QueryKeys.AdminLicenses]);
        await queryClient.invalidateQueries([QueryKeys.UserGroups]);
    };

    /**
     * Trigger an update two seconds after last keypress.
     */
    const debouncePromise = useCallback(AwesomeDebouncePromise(update, 2000), []);

    /**
     * Trigger an update to the form data state.
     * @param field
     * @param value
     */
    const onInputChange = async (field, value) => {
        const formData: ILicenseDetails = { ...current };
        formData[field] = value;
        updateCurrent(formData);
        stateRef.current = { ...formData };
        await debouncePromise();
    };

    /**
     * Add a new audience instance, adding a set of tag instances.
     * @param value
     */
    const createAudienceInstance = async (value) => {
        const audienceId = value[0];
        setUpdating(true);
        const service = new LicenseService();
        const res = await service.createAudienceInstance(audienceId, current?.licenseId);
        setUpdating(false);
        if (!res.hasErrors) {
            notification.success({ message: "Audience added." });
        } else {
            notification.error({ message: "Error adding audience." });
        }
        await queryClient.invalidateQueries([QueryKeys.AdminLicenses]);
        await queryClient.invalidateQueries([QueryKeys.UserGroups]);
    };

    /**
     * Remove an audience instance from a license.
     * @param audienceInstanceId
     */
    const deleteAudienceInstance = async (audienceInstanceId) => {
        const newLicense: ILicenseDetails = { ...current };
        newLicense.audienceInstances = newLicense.audienceInstances.filter(
            (x) => x.audienceInstanceId !== audienceInstanceId
        );
        updateCurrent(newLicense);
        stateRef.current = newLicense;
        setUpdating(true);
        const service = new LicenseService();
        const res = await service.deleteAudienceInstance(audienceInstanceId);
        setUpdating(false);
        if (!res.hasErrors) {
            notification.success({ message: "Audience removed." });
        } else {
            notification.error({ message: "Error removing audience." });
        }
        await queryClient.invalidateQueries([QueryKeys.AdminLicenses]);
        await queryClient.invalidateQueries([QueryKeys.UserGroups]);
    };

    /**
     * Update the values of a given tag instance.
     * @param instance
     * @param values
     */
    const updateTagInstance = async (instance: IAudienceTagInstanceDetails, values) => {
        const updatedLicense = { ...current };
        const instanceIndex = updatedLicense.audienceInstances?.findIndex(
            (x) => x.audienceInstanceId === instance.audienceInstanceId
        );
        if (instanceIndex !== -1) {
            const index = updatedLicense.audienceInstances[instanceIndex]?.audienceTagInstances?.findIndex(
                (x) => x.audienceTagInstanceId === instance.audienceTagInstanceId
            );
            if (index !== -1) {
                updatedLicense.audienceInstances[instanceIndex].audienceTagInstances[index].values = values;
                updateCurrent(updatedLicense);
                stateRef.current = { ...updatedLicense };
                await debouncePromise();
            }
        }
    };

    /**
     * Delete license.
     */
    const deleteLicense = async () => {
        await deleteCurrent(current);
        setUpdating(true);
        const service = new LicenseService();
        const deleteResult = await service.delete(current?.licenseId);
        updateCurrent(null);
        stateRef.current = null;
        setUpdating(false);
        if (!deleteResult.hasErrors) {
            notification.success({ message: "License deleted." });
        } else {
            notification.error({ message: "Error deleting license." });
        }
        await queryClient.invalidateQueries([QueryKeys.AdminLicenses]);
        await queryClient.invalidateQueries([QueryKeys.UserGroups]);
    };

    /**
     * Get available audiences
     */
    const getAudiences = async () => {
        const service = new LicenseService();
        const audienceResult = await service.getAudiences();
        if (!audienceResult.hasErrors) {
            setAudiences(audienceResult.value);
        } else if (!audienceResult.isForbidden) {
            notification.error({ message: "Error getting audiences." });
        }
    };

    /**
     * Get tag info for a given instance.
     * @param tagInstance
     */
    const getTag = (tagInstance: IAudienceTagInstanceDetails) => {
        return audiences
            ?.find((x) => x.audienceId === tagInstance.audienceId)
            ?.tags?.find((y) => y.audienceTagId === tagInstance.audienceTagId);
    };

    /**
     * Lazy-load unassigned assets into the transfer cascader options.
     * @param selectedOptions
     */
    const loadUnassigned = async (selectedOptions: IAssetOption[]) => {
        if (selectedOptions[0]?.value !== "unassigned") return;
        const targetOption = selectedOptions[selectedOptions.length - 1];
        targetOption.loading = true;
        const licenseService = new LicenseService();
        const result = await licenseService.getUnassignedAssets(current?.productId);
        targetOption.loading = false;
        if (!result.hasErrors && result.value?.length > 0) {
            targetOption.children = result.value.map((x) => {
                return { label: x.nickName, value: x.assetId };
            });
            setAssetOptions([...assetOptions]);
            return;
        }
        targetOption.children = [
            {
                label: <Empty description={"No assets."} image={Empty.PRESENTED_IMAGE_SIMPLE} />,
                value: "none",
            },
        ];
        setAssetOptions([...assetOptions]);
    };

    /**
     * Add a new asset, or use an existing unassigned asset.
     * @param value
     */
    const addAsset = async (value) => {
        const useUnassigned = value[0] === "unassigned";
        if (value[1] === "none" || value[0] === null) return;

        let quantity = 1;
        if (value[1].length > 4 && value[1].substring(0, 4) === "new-") {
            quantity = parseInt(value[1].substring(4));
            if (isNaN(quantity)) return;
        }
        const newLicense: ILicenseDetails = { ...current };
        for (let i = 0; i < quantity; i++) {
            newLicense.assets.push({
                assetId: useUnassigned ? value[1] : 0,
                serial: "",
                nickName: "",
                status: "accepted",
                tags: [{ key: "pro", value: "false" }],
                licenseId: current?.licenseId,
                productId: current?.productId,
            });
        }
        updateCurrent(newLicense);
        stateRef.current = newLicense;
        await update();
    };

    /**
     * Remove an asset. This may create an unassigned asset, or "orphan" depending on the asset state.
     * @param value
     */
    const removeAsset = async (value) => {
        const newLicense: ILicenseDetails = { ...current };
        newLicense.assets = newLicense.assets.filter((x) => x.assetId !== value);
        updateCurrent(newLicense);
        stateRef.current = newLicense;
        await update();
    };

    /**
     * Transfer an asset to a different license.
     * @param assetId
     * @param value
     */
    const transferAsset = async (assetId: string, value) => {
        if (value.length < 2) return;
        if (value[1] === "none") return;
        const newLicense: ILicenseDetails = { ...current };
        const index = newLicense.assets.findIndex((x) => x.assetId === assetId);
        // Create a copy of the asset with the new license ID
        const assetCopy = {
            ...newLicense.assets[index],
            licenseId: value[1],
        };

        newLicense.assets.push(assetCopy);
        updateCurrent(newLicense);
        stateRef.current = newLicense;
        await update();
    };

    const audienceOptions = audiences?.map((x) => {
        return { label: x.name, value: x.audienceId };
    });

    const transferOptions: ICascaderOption[] = [];
    const uniqueLicensees = [...new Set((Array.isArray(licenses) ? licenses : []).map((x) => x.licensee))];
    uniqueLicensees.forEach((x) => {
        let licenseOptions: ICascaderOption[] = licenses
            ?.filter((y) => x === y.licensee && y.licenseId !== current?.licenseId)
            .map((x) => {
                return { label: x.name, value: x.licenseId };
            });
        if (licenseOptions.length === 0) {
            licenseOptions = [
                {
                    label: <Empty description={"No available licenses."} image={Empty.PRESENTED_IMAGE_SIMPLE} />,
                    value: "none",
                },
            ];
        }

        // Place current licensee at the top of the transfer options list.
        if (x == current?.licensee) {
            transferOptions.unshift({ label: x, value: x, children: licenseOptions });
        } else {
            transferOptions.push({ label: x, value: x, children: licenseOptions });
        }
    });

    /**
     * Update an asset status
     * @param asset
     * @param val
     */
    const changeAssetStatus = async (asset: IAssetDetails, val: string) => {
        if (val === "" || val === "unassigned") return;
        if (!["accepted", "production", "shipped", "fault"].includes(val)) return;
        const newLicense: ILicenseDetails = { ...current };
        const index = newLicense.assets.findIndex((x) => x.assetId === asset.assetId);
        newLicense.assets[index].status = val;
        updateCurrent(newLicense);
        stateRef.current = newLicense;
        await update();
    };

    /**
     * Update an asset pro version tag.
     * @param asset
     * @param val
     */
    const changeAssetProTag = async (asset: IAssetDetails, val: boolean) => {
        const newLicense: ILicenseDetails = { ...current };
        const index = newLicense.assets.findIndex((x) => x.assetId === asset.assetId);
        const tagIndex = newLicense.assets[index].tags?.findIndex((x) => x.key === "pro");
        if (tagIndex === -1) {
            newLicense.assets[index].tags.push({ key: "pro", value: val.toString() });
        } else {
            newLicense.assets[index].tags[tagIndex].value = val.toString();
        }
        updateCurrent(newLicense);
        stateRef.current = newLicense;
        await update();
    };

    /**
     * Return true if the asset is marked as a pro version.
     * @param asset
     */
    const getProAsset = (asset: IAssetDetails) => {
        const tagIndex = asset?.tags?.findIndex((x) => x.key === "pro");
        return isNil(asset.tags[tagIndex]?.value) ? false : asset.tags[tagIndex].value === "true";
    };

    const productName = definitions.products?.find((x) => x.productId === current?.productId)?.label;

    useEffect(() => {
        getAudiences().then();
    }, []);

    return !current ? null : (
        <section>
            <h4>Details {fetching && <LoadingOutlined />}</h4>
            <Form
                className="inspector-form"
                initialValues={{
                    name: current?.name,
                    serial: current?.serial,
                    description: current?.description,
                    capacity: current?.capacity,
                    startDate: current?.startDate,
                    endDate: current?.endDate,
                }}
            >
                <Form.Item label="Name">
                    <Input
                        name={"name"}
                        value={current?.name}
                        onChange={(e) => onInputChange(e.target.name, e.target.value)}
                    />
                </Form.Item>

                <Form.Item label="Serial">
                    <InspectorInfoField>{current?.serial}</InspectorInfoField>
                </Form.Item>

                <Form.Item label="Product">
                    <Tag color={"magenta"}>{productName}</Tag>
                </Form.Item>

                <Form.Item label="Description">
                    <Input
                        name={"description"}
                        value={current?.description}
                        onChange={(e) => onInputChange(e.target.name, e.target.value)}
                    />
                </Form.Item>

                <Form.Item label="Capacity">
                    <InputNumber
                        name={"capacity"}
                        onChange={(value) => onInputChange("capacity", value)}
                        placeholder="0"
                        value={current?.capacity}
                        addonAfter={"Users"}
                    />
                </Form.Item>

                <Form.Item label="Start Date">
                    <InspectorInfoField>{moment(current?.startDate).format("MMM Do YYYY")}</InspectorInfoField>
                </Form.Item>
                <Form.Item label="Renewal Date">
                    <DatePicker
                        onChange={(date) => onInputChange("endDate", date?.toISOString())}
                        picker="date"
                        allowClear={false}
                        placeholder="Select renewal date."
                        defaultValue={moment(current?.endDate)}
                        format={"MMM Do YYYY"}
                    />
                </Form.Item>
                <Popconfirm
                    placement="top"
                    title="Delete this license and associated group? This cannot be undone."
                    okText="Yes, delete the license"
                    cancelText="Cancel"
                    className="confirmation"
                    onConfirm={deleteLicense}
                    cancelButtonProps={{ className: "secondary" }}
                >
                    <Button
                        style={{ width: "100%" }}
                        icon={<CloseCircleFilled />}
                        className="secondary danger"
                        loading={updating}
                        title="Delete license"
                    >
                        Delete License
                    </Button>
                </Popconfirm>
            </Form>
            <Divider />

            <div style={{ display: "inline-flex" }}>
                <h4>
                    Assets {fetching && <LoadingOutlined />}
                    {current?.assets.length > 0 && (
                        <Badge
                            className="asset-count"
                            count={current?.assets.length}
                            style={{ marginLeft: 10, backgroundColor: "#00bbff", border: "none" }}
                        />
                    )}
                </h4>
                {current?.assets && current?.assets?.length === 0 && (
                    <Tag style={{ margin: "0px 10px 10px 10px" }} color="gray">
                        None
                    </Tag>
                )}
            </div>
            {current?.assets && current?.assets?.length > 0 && (
                <Collapse accordion className={"assets-collapse"}>
                    {current?.assets?.map((a, index) => (
                        <Panel
                            header={
                                <div className={"asset-header"}>
                                    <p>
                                        {a.nickName !== "" ? a.nickName : <LoadingOutlined />}
                                        <Tag style={{ marginLeft: "10px" }} color="magenta">
                                            {productName}
                                        </Tag>
                                    </p>
                                    <Popconfirm
                                        placement="top"
                                        title={
                                            "Remove this asset?" +
                                            (a.status !== "accepted" ? " This will create an unassigned asset." : "")
                                        }
                                        okText="Yes"
                                        cancelText="Cancel"
                                        className="confirmation"
                                        onConfirm={() => removeAsset(a.assetId)}
                                        cancelButtonProps={{ className: "secondary" }}
                                    >
                                        <CloseCircleFilled title={"Remove asset"} />
                                    </Popconfirm>
                                </div>
                            }
                            key={"asset-panel-" + index}
                        >
                            <Form
                                style={{ width: "100%" }}
                                key={"asset-inspector-" + a.assetId}
                                className="inspector-form"
                                initialValues={{}}
                            >
                                <Form.Item label="Serial">
                                    <InspectorInfoField>{a.serial}</InspectorInfoField>
                                </Form.Item>
                                <Form.Item label="Pro">
                                    <Switch
                                        checkedChildren="Yes"
                                        unCheckedChildren="No"
                                        defaultChecked={getProAsset(a)}
                                        disabled={a.status !== "accepted"}
                                        onChange={(checked) => changeAssetProTag(a, checked)}
                                    />
                                </Form.Item>
                                <Form.Item label="Status">
                                    {a.status === "unassigned" ? (
                                        <InspectorInfoField>{toPascalCase(a.status)}</InspectorInfoField>
                                    ) : (
                                        <Select
                                            value={toPascalCase(a.status)}
                                            onChange={(val) => changeAssetStatus(a, val)}
                                        >
                                            {a.status !== "unassigned" ||
                                                "shipped" ||
                                                ("fault" && (
                                                    <Option key={"status-accepted"} value="accepted">
                                                        Accepted
                                                    </Option>
                                                ))}
                                            {a.status !== "unassigned" && (
                                                <Option key={"status-production"} value="production">
                                                    Production
                                                </Option>
                                            )}
                                            {a.status !== "unassigned" && (
                                                <Option key={"status-shipped"} value="shipped">
                                                    Shipped
                                                </Option>
                                            )}
                                            {a.status === "shipped" && (
                                                <Option key={"status-fault"} value="fault">
                                                    Fault
                                                </Option>
                                            )}
                                        </Select>
                                    )}
                                </Form.Item>
                            </Form>
                            <Divider />
                            <Cascader
                                options={transferOptions}
                                value={[]}
                                onChange={(x) => transferAsset(a.assetId, x)}
                                notFoundContent={<Tag color={"gray"}>No transfer options available</Tag>}
                            >
                                <Button
                                    style={{ width: "100%" }}
                                    icon={<ExportOutlined />}
                                    loading={updating}
                                    className="secondary"
                                    title="Transfer asset to another license"
                                >
                                    Transfer
                                </Button>
                            </Cascader>
                        </Panel>
                    ))}
                </Collapse>
            )}
            <Cascader options={assetOptions} loadData={loadUnassigned} value={[]} onChange={addAsset}>
                <Button
                    style={{ width: "100%" }}
                    icon={<PlusCircleOutlined />}
                    loading={updating}
                    className="secondary"
                    title="Add asset"
                >
                    Add Asset
                </Button>
            </Cascader>
            <Divider />

            <div style={{ display: "inline-flex" }}>
                <h4>
                    Audiences {fetching && <LoadingOutlined />}
                    {current?.audienceInstances.length > 0 && (
                        <Badge
                            className="audience-count"
                            count={current?.audienceInstances.length}
                            style={{ marginLeft: 10, backgroundColor: "#00bbff", border: "none" }}
                        />
                    )}
                </h4>
                {current?.audienceInstances && current?.audienceInstances?.length === 0 && (
                    <Tag style={{ margin: "0px 10px 10px 10px" }} color="gray">
                        None
                    </Tag>
                )}
            </div>
            {current?.audienceInstances?.length > 0 && (
                <Collapse accordion className={"audiences-collapse"}>
                    {current?.audienceInstances?.map((a, index) => (
                        <Panel
                            header={
                                <div className={"audience-header"}>
                                    <p>
                                        {a.audienceName !== "" ? a.audienceName : <LoadingOutlined />}
                                        <Tag style={{ marginLeft: "10px" }} color="magenta">
                                            {productName}
                                        </Tag>
                                    </p>
                                    <Popconfirm
                                        placement="top"
                                        title="Remove this audience?"
                                        okText="Yes"
                                        cancelText="Cancel"
                                        className="confirmation"
                                        onConfirm={() => deleteAudienceInstance(a.audienceInstanceId)}
                                        cancelButtonProps={{ className: "secondary" }}
                                    >
                                        <CloseCircleFilled title={"Remove audience"} />
                                    </Popconfirm>
                                </div>
                            }
                            key={"audience-panel-" + index}
                        >
                            {a.activities?.map((x, i) => (
                                <Tag color="blue" style={{ margin: "2px" }}>
                                    {definitions?.activities?.find((y) => y.activityId === x.id)?.label}
                                </Tag>
                            ))}
                            {a.audienceTagInstances?.length > 0 && <Divider />}
                            {a.audienceTagInstances?.map((x, i) => (
                                <ModuleAudienceRow
                                    tag={getTag(x)}
                                    instance={x}
                                    handleChange={(tagInstance, values) => updateTagInstance(tagInstance, values)}
                                    key={i}
                                    editable
                                />
                            ))}
                        </Panel>
                    ))}
                </Collapse>
            )}
            {audienceOptions.filter(
                (x) => current?.audienceInstances?.findIndex((y) => y.audienceId === x.value) === -1
            ).length > 0 && (
                <Cascader
                    options={audienceOptions.filter(
                        (x) => current?.audienceInstances?.findIndex((y) => y.audienceId === x.value) === -1
                    )}
                    value={[]}
                    onChange={createAudienceInstance}
                >
                    <Button
                        style={{ width: "100%" }}
                        icon={<PlusCircleOutlined />}
                        loading={updating}
                        className="secondary"
                        title="Add audience"
                    >
                        Add Audience
                    </Button>
                </Cascader>
            )}
        </section>
    );
};

export default LicenseEditor;
